import Base64 from "app/assets/javascripts/upload/base64"
import { gen_file_title, file_basename } from "app/assets/javascripts/upload/upload_common"
import {
  CONVERSION_POLL_INTERVAL,
  PROGRESS_DELAY,
  QUALITY_CHECK_POLL_INTERVAL,
  QUALITY_CHECK_RETRY_ATTEMPTS,
  UPLOAD_PROGRESS_INTERVAL,
} from "./constants"
import { get, post, putFile, postFormData } from "./api"
import { v4 as uuidv4 } from "uuid"
import { logUploadFlowEvent, t, UserError } from "frontend/utils"
import { isTestEnvironment } from "./testing"

const MAX_FILE_SIZE = 300 * 1024 * 1024 // maximum allowed size in bytes. Replaces user.max_presentation_size

export const FILE_STATUS = {
  QUEUED: -1,
  IN_PROGRESS: -2,
  ERROR: -3,
  COMPLETE: -4,
  CANCELLED: -5,
}

const fbasename = function (filename) {
  let str = "" + filename
  let brkpoint = str.lastIndexOf(".")
  brkpoint = brkpoint != -1 ? brkpoint : str.length
  return str.substr(0, brkpoint)
}

const fextension = function (filename) {
  return /[.]/.exec(filename) ? /[^.]+$/.exec(filename)[0].toLowerCase() : undefined
}

const generate_s3_key = function (filename, key) {
  let ts = new Date().getTime(),
    fn = fbasename(filename),
    ex = fextension(filename)
  fn = fn.toLowerCase().replace(/[^a-z0-9\-\.]+/g, "")
  let r = fn + "-u-" + ts + "-" + key + "-u" + "." + ex
  return r
}

const getUploadTarget = () => {
  return window.upload_config.urls.upload_target
}

const getRegisterURL = () => {
  return window.upload_config.urls.register_upload_attempt_for_redesigned
}

const getUploadCompleteURL = () => {
  return window.upload_config.urls.complete_upload
}

const getRecaptchaVerificationURL = () => {
  return window.upload_config.urls.recaptcha_verification
}

const getConversionStatusUrl = (queryParams) => {
  return `${window.upload_config.urls.conversion_status}${queryParams}`
}

const getUploadType = () => {
  return window.upload_config.upload_type
}

const getAWSAccessKey = () => {
  return window.upload_config.aws_access_key
}

export const getUploadBucket = () => {
  return window.upload_config.filestack_bucket
}

export const verifyRecaptcha = async (string) => {
  const result = await post(`${getRecaptchaVerificationURL()}?g-recaptcha-response-data=${string}`)

  if (result.data.event === "invalid_recaptcha") {
    throw new Error("invalid_recaptcha")
  }

  return result
}

export const createUploadInfo = function (file) {
  let id = "pre_slideshow_" + uuidv4()
  let fileName = file.filename
  let s3key = file.key || generate_s3_key(fileName, "b")
  let uploadTarget = getUploadTarget()
  return {
    file_id: id,
    ppt_location: uploadTarget + "/" + s3key,
    filename: fileName,
    slideshow_filename: Base64.encode(fileName),
    source: "interactive_upload",
    upload_type: getUploadType(),
    slideshow_id: "",
    title: gen_file_title(file_basename(fileName)).escapeHTML(),
    description: "",
    top_cat: "",
    private_type: "public",
    tag_text: "",
    key: s3key,
    flash: 0,
    size: file.size,
    filestatus: FILE_STATUS.QUEUED,
  }
}

const isValidExtension = function (filename, extensions) {
  let extArray = $.map(extensions.split(" "), function (str) {
    return str.substring(2, str.length - 1)
  })
  let file_ext = fextension(filename)
  if ($.inArray(file_ext, extArray) == -1) {
    return false
  }
  return true
}

export const isPresentation = function (file) {
  return isValidExtension(file.filename, "*.ppt; *.pdf; *.doc; *.pptx; *.ppsx; *.potx; *.docx; *.dotx;")
}

export const verifyFiles = (files) => {
  files.forEach((file) => {
    // If you throw any error in this function it will reject the file selection.
    // The error message will be displayed to the user as an alert.
    if (file.size > MAX_FILE_SIZE) {
      logUploadFlowEvent("invalid_size")

      throw new Error(t("upload.error.file_size"))
    }

    if (!isPresentation(file)) {
      logUploadFlowEvent("invalidext_error")

      throw new Error(t("upload.error.file_type"))
    }

    logUploadFlowEvent("file_verified")
  })
}

const getConversionStatus = async (fileId, resolve, reject, interval = null) => {
  const response = await get(getConversionStatusUrl(`?file_id=${fileId}&_=${fileId.match(/\d+/)[0]}`))

  const event = response.data && response.data.event
  const slideshowType = response.data && response.data.slideshow_type

  logUploadFlowEvent("file_converting")

  if (response.status !== 200 && !event) {
    clearInterval(interval)

    logUploadFlowEvent("slideshow_conversion_failed")

    reject("request_failed")
  }

  if (event === "slideshow_converted") {
    clearInterval(interval)

    logUploadFlowEvent("slideshow_converted")

    resolve(slideshowType)
  } else if (event !== "slideshow_converting" && event !== "slideshow_queued") {
    clearInterval(interval)

    logUploadFlowEvent("slideshow_conversion_failed")

    reject(event)
  }
}

const pollForConversionStatus = async (fileId) => {
  return new Promise((resolve, reject) => {
    getConversionStatus(fileId, resolve, reject)
    const interval = setInterval(async () => {
      getConversionStatus(fileId, resolve, reject, interval)
    }, CONVERSION_POLL_INTERVAL)
  })
}

const showPollProgress = (setProgress, start, end) => {
  let difference = (end - start) / 2
  let current = start
  const interval = setInterval(() => {
    current = current + difference
    setProgress(current, t("upload.form.progress.optimizing"))
    difference = difference / 2
  }, UPLOAD_PROGRESS_INTERVAL)
  return interval
}

const getUploadProgressCallback = (totalBytes, start, finish, setProgress) => {
  const difference = finish - start
  return (bytesUploaded) => {
    setProgress(
      (bytesUploaded / totalBytes) * difference + start,
      t("upload.form.progress.upload", [bytesToMB(bytesUploaded), bytesToMB(totalBytes)])
    )
  }
}

export const bytesToMB = (bytes) => (bytes / 1000000).toPrecision(2)

const uploadFile = async (registrationData, file, uploadProgressCallback) => {
  const useDirectUpload = Boolean(registrationData.url)

  if (useDirectUpload) {
    await putFile(registrationData.url, file.originalFile, {}, uploadProgressCallback)
  } else {
    await postFormData(
      getUploadTarget(),
      {
        key: registrationData.s3_key,
        AWSAccessKeyId: getAWSAccessKey(),
        acl: "private",
        success_action_redirect: null,
        policy: registrationData.policy,
        signature: registrationData.signature,
        file: file.originalFile,
      },
      uploadProgressCallback
    )
  }
}

const getQualityCheckError = (reason) => {
  const prefix = "upload.form.error."

  switch (reason) {
    case "spam": {
      return `${prefix}spam_and_copyright`
    }
    case "duplicate": {
      return `${prefix}duplicate`
    }
    default: {
      return `${prefix}quality`
    }
  }
}

const checkUploadQuality = async (filename, uploadData, retries = 0) => {
  let responseData

  try {
    const qualityCheckUrl = window.upload_config.urls.incentivized
    const uploadId = uploadData.data.slideshow_id
    const { key, id } = getIncentivizedUploadData()
    const postData = {
      download_id: id,
      download_key: key,
      upload_id: uploadId,
    }

    const response = await post(qualityCheckUrl, postData)

    responseData = response.data
  } catch (e) {
    console.warn("Upload quality check failed: ", e)

    logUploadFlowEvent("quality_check_failed", { reason: "request failed" })

    // Show the user the generic "Something went wrong" error
    throw new Error(e)
  }

  if (responseData.pending) {
    if (retries > QUALITY_CHECK_RETRY_ATTEMPTS) {
      throw new Error("Timed out - pending too long")
    }

    console.warn('Quality check "pending" - retrying...')

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(checkUploadQuality(filename, uploadData, retries + 1))
      }, QUALITY_CHECK_POLL_INTERVAL)
    })
  }

  if (!responseData.download) {
    logUploadFlowEvent("quality_check_failed", { reason: "ineligible" })

    const errorKey = getQualityCheckError(responseData.reason)
    const errorMessage = t(errorKey, { filename })

    throw new UserError(errorMessage)
  }
}

export const startFileUpload = async (data, file, setProgress) => {
  logUploadFlowEvent("upload_start")

  const isCloudUpload = Boolean(file.key)
  setProgress(0, t("upload.form.progress.creating"))
  const registration = await postFormData(getRegisterURL(), data)
  setProgress(10, t("upload.form.progress.creating"))
  const uploadProgressCallback = getUploadProgressCallback(file.size, 20, 60, setProgress)
  if (!isCloudUpload) {
    await uploadFile(registration.data, file, uploadProgressCallback)
  }
  const completionResponse = await postFormData(getUploadCompleteURL(), data)

  logUploadFlowEvent(completionResponse.status === 200 ? "success" : "error")
  logUploadFlowEvent("complete")

  const interval = showPollProgress(setProgress, 60, 100)
  let slideshowType = null
  try {
    slideshowType = await pollForConversionStatus(data.file_id)
  } catch (e) {
    clearInterval(interval)
    throw new Error(e)
  }
  clearInterval(interval)

  const slideshowData = { ...completionResponse.data.data, slideshowType }

  if (isIncentivizedUpload() && !isTestEnvironment()) {
    logUploadFlowEvent("quality_check_start")
    setProgress(95, t("upload.form.progress.verifying_quality"))

    await checkUploadQuality(data.filename, completionResponse.data)

    logUploadFlowEvent("quality_check_success")
  }

  setProgress(100, t("upload.form.progress.optimizing"))

  // Ensure the progress bar goes to 100
  await new Promise((resolve) => setTimeout(resolve, PROGRESS_DELAY))
  return slideshowData
}

const updateSlideshowDetails = (details) => {
  return post(window.upload_config.urls.save_details, details)
}

const publishUpload = (data) => {
  return post(window.upload_config.urls.publish, data)
}

export const publishSlideshow = async (data) => {
  const updateResponse = await updateSlideshowDetails(data)
  if (!updateResponse.data || updateResponse.data.event !== "details_saved") {
    logUploadFlowEvent("publish_error")

    throw new Error(updateResponse.data.event)
  }
  const publishResponse = await publishUpload(data)

  if (!publishResponse.data || publishResponse.data.event !== "slideshow_published") {
    logUploadFlowEvent("publish_error")

    throw new Error(publishResponse.data.event)
  }

  logUploadFlowEvent("slideshow_published")

  return publishResponse.data.data
}

export const createPublishData = (uploadData, formValues) => {
  const privacy = formValues.get("privacy")
  const allowDownloadStr = formValues.get("allow_download") ? "ALL" : "NONE"
  /**
   * Default for allow_embeds is false in private state - user can change using checkbox
   * For public or limited, allow_embeds must be true by default - user cannot change this setting
   */
  return {
    ...uploadData,
    allow_download: privacy === "onlyme" ? "NONE" : allowDownloadStr,
    allow_clips: privacy === "public" ? formValues.get("allow_clips") : false,
    allow_embeds: privacy === "public" ? formValues.get("allow_embed") : false,
    title: formValues.get("title"),
    description: formValues.get("description"),
    license: formValues.get("license"),
    top_cat: formValues.get("category"),
    private_type: privacy,
    tag_text: formValues.get("tags").join(","),
  }
}

export const isIncentivizedUpload = () => {
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)

  return Boolean(urlParams.get("download_id") && window.upload_config.incentivized)
}

export const getIncentivizedUploadData = () => {
  return window.upload_config.incentivized
}
